/**
 * O painel de conversa — shot `1j`.
 *
 * Só apresentação: recebe a conversa pronta e devolve intenções. Quem fala com a
 * API é o `Assistente`.
 *
 * Quatro desvios do shot, todos registrados (spec e §9 do PROJECT-OVERVIEW):
 *  - o avatar usa `ph-head-circuit`, o mesmo do FAB (Fábio, 05/10/2026);
 *  - o subtítulo e os chips não trazem Compliance — o chat não tem esse especialista;
 *  - `fechar` ENCERRA a conversa; quem quer só recolher usa `minimizar`;
 *  - o botão de enviar fica esmaecido com o campo vazio (o shot o mostra preto) —
 *    a confirmar com o Eduardo.
 *
 * ⚠️ Sem `<header>`/`<footer>`: computam como `banner`/`contentinfo` e competem com
 * os do frame do módulo (CLAUDE.md).
 */
import { memo, useEffect, useId, useLayoutEffect, useRef } from 'react'

import type { Conversa, ItemDaConversa } from './conversa'
import { MarkdownLeve } from './MarkdownLeve'
import { atribuicao, LIMITE_DE_CARACTERES, TEXTOS } from './textos'

const TOM_DO_AVISO = {
  atencao: 'bg-warning-100 text-warning-700',
  perigo: 'bg-danger-100 text-danger-700',
} as const

const BOTAO_DE_ICONE = 'text-icon-muted hover:text-text-primary rounded-control duration-fast p-1'

/**
 * ⚠️ Alinhamento e respiro aqui são `self-*` e `gap`, NUNCA margem. O `base.css` do
 * design system declara `p{margin:0}` fora de camada, e CSS sem camada vence todo
 * utilitário do Tailwind: um `ml-auto` ou `mt-1.5` num `<p>` compila e não faz nada.
 * O jsdom não aplica CSS — quem guarda isto é `e2e/fluxo.assistente.e2e.ts`.
 *
 * `memo`: os itens da conversa são imutáveis, e o painel redesenha a cada tecla do
 * rascunho. Sem isto, cada tecla reprocessaria o markdown de todas as respostas.
 */
const Item = memo(function Item({ item }: { item: ItemDaConversa }) {
  if (item.tipo === 'usuario') {
    return (
      <p className="bg-iox-navy text-text-inverse type-body max-w-[85%] self-end rounded-lg px-4 py-2.5 break-words whitespace-pre-wrap">
        {item.texto}
      </p>
    )
  }

  if (item.tipo === 'aviso') {
    return (
      <p className={`rounded-field type-sm px-4 py-3 ${TOM_DO_AVISO[item.tom]}`}>{item.texto}</p>
    )
  }

  return (
    <div className="flex flex-col items-start gap-1.5">
      <div className="border-border-subtle bg-surface-card text-text-body type-body flex max-w-[90%] flex-col gap-2 rounded-lg border px-4 py-3 break-words">
        <MarkdownLeve texto={item.texto} />
      </div>
      <p className="type-sm text-text-muted">{atribuicao(item.especialistas)}</p>
    </div>
  )
})

export function PainelDoAssistente({
  conversa,
  especialidades,
  sugestoes,
  rascunho,
  aoMudarRascunho,
  aoEnviar,
  aoMinimizar,
  aoFechar,
}: {
  conversa: Conversa
  /** Rótulos do subtítulo — só os módulos ligados para o cliente. */
  especialidades: readonly string[]
  /** Textos dos chips — idem. */
  sugestoes: readonly string[]
  /**
   * O que está sendo digitado. Mora em quem monta o painel, e não aqui: o painel
   * desmonta ao minimizar, e a pergunta pela metade não pode sumir com ele.
   */
  rascunho: string
  aoMudarRascunho: (texto: string) => void
  aoEnviar: (texto: string) => void
  aoMinimizar: () => void
  aoFechar: () => void
}) {
  const campo = useRef<HTMLTextAreaElement>(null)
  const fim = useRef<HTMLDivElement>(null)
  const idDoTitulo = useId()

  // Abrir foca o campo. É efeito de FOCO, não de dados: a BR-25 não se aplica.
  useEffect(() => {
    campo.current?.focus()
  }, [])

  // Mantém a última mensagem à vista. O `?.()` é pelo jsdom, que não implementa
  // `scrollIntoView`.
  useEffect(() => {
    fim.current?.scrollIntoView?.({ block: 'end' })
  }, [conversa.itens.length, conversa.pendente])

  // O campo cresce com o texto até o teto do `max-h-28`; daí em diante, rola. A
  // altura vem do CONTEÚDO medido, não é um valor de design. `offsetHeight -
  // clientHeight` são as bordas, que o `scrollHeight` não conta.
  useLayoutEffect(() => {
    const elemento = campo.current
    if (!elemento) return
    elemento.style.height = 'auto'
    const bordas = elemento.offsetHeight - elemento.clientHeight
    elemento.style.height = `${elemento.scrollHeight + bordas}px`
  }, [rascunho])

  /**
   * `doCampo`: só o envio do PRÓPRIO campo limpa o rascunho. Clicar num chip manda o
   * texto do chip e deixa intacto o que a pessoa estava digitando.
   */
  function enviar(valor: string, doCampo: boolean) {
    const limpo = valor.trim()
    if (limpo === '' || conversa.pendente) return
    aoEnviar(limpo)
    if (doCampo) aoMudarRascunho('')
    // O chip e o botão de enviar ficam desabilitados durante a espera; o foco não
    // pode ficar preso num controle morto.
    campo.current?.focus()
  }

  return (
    <div
      role="dialog"
      aria-labelledby={idDoTitulo}
      onKeyDown={(evento) => {
        // `isComposing`: o Esc que CANCELA um acento ou um caractere de IME não é
        // um pedido para recolher o painel.
        if (evento.key === 'Escape' && !evento.nativeEvent.isComposing) aoMinimizar()
      }}
      // 48px = 2 × o recuo de 24px (`right-6`/`bottom-6`): o painel nunca passa da janela.
      className="border-border-subtle bg-surface-card rounded-card shadow-panel fixed right-6 bottom-6 z-40 flex h-140 max-h-[calc(100dvh-48px)] w-105 max-w-[calc(100vw-48px)] flex-col overflow-hidden border print:hidden"
    >
      <div className="border-border-subtle flex items-center gap-3 border-b px-5 py-4">
        <span
          className="bg-gradient-brand text-text-inverse rounded-control flex size-10 shrink-0 items-center justify-center"
          aria-hidden="true"
        >
          <i className="ph ph-head-circuit text-xl" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={idDoTitulo} className="type-h4 text-text-primary">
            {TEXTOS.titulo}
          </h2>
          {especialidades.length > 0 && (
            <p className="type-sm text-text-muted">{especialidades.join(' · ')}</p>
          )}
        </div>
        <button
          type="button"
          aria-label={TEXTOS.minimizar}
          title={TEXTOS.minimizar}
          onClick={aoMinimizar}
          className={BOTAO_DE_ICONE}
        >
          <i className="ph ph-arrows-in-simple text-xl" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={TEXTOS.fechar}
          // O X ENCERRA a conversa; a dica diz isso antes do clique.
          title={TEXTOS.fechar}
          onClick={aoFechar}
          className={BOTAO_DE_ICONE}
        >
          <i className="ph ph-x text-xl" aria-hidden="true" />
        </button>
      </div>

      {/* `role="log"` já é uma região viva educada: cada balão novo é anunciado. */}
      <div
        role="log"
        aria-label={TEXTOS.conversa}
        // Focável para a conversa poder ser rolada pelo teclado.
        tabIndex={0}
        className="bg-subtle flex flex-1 flex-col gap-3 overflow-y-auto px-5 py-4"
      >
        {conversa.itens.map((item) => (
          <Item key={item.id} item={item} />
        ))}
        {conversa.pendente && <p className="type-sm text-text-muted">{TEXTOS.consultando}</p>}
        <div ref={fim} />
      </div>

      {sugestoes.length > 0 && (
        <div role="group" aria-label="Sugestões" className="bg-subtle flex flex-wrap gap-2 px-5 pb-3">
          {sugestoes.map((sugestao) => (
            <button
              key={sugestao}
              type="button"
              disabled={conversa.pendente}
              // Clique explícito do usuário: é um envio como outro qualquer (BR-25).
              onClick={() => enviar(sugestao, false)}
              className="border-border-subtle bg-surface-card text-text-body hover:border-border-strong rounded-control type-sm duration-fast border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sugestao}
            </button>
          ))}
        </div>
      )}

      <form
        className="border-border-subtle flex items-end gap-3 border-t px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault()
          enviar(rascunho, true)
        }}
      >
        <textarea
          ref={campo}
          rows={1}
          value={rascunho}
          maxLength={LIMITE_DE_CARACTERES}
          aria-label={TEXTOS.campo}
          placeholder={TEXTOS.campo}
          onChange={(evento) => aoMudarRascunho(evento.target.value)}
          onKeyDown={(evento) => {
            // `isComposing`: o Enter que CONFIRMA um acento ou um caractere de IME
            // não é um envio.
            if (evento.key === 'Enter' && !evento.shiftKey && !evento.nativeEvent.isComposing) {
              evento.preventDefault()
              enviar(rascunho, true)
            }
          }}
          // `rounded-2xl` (24px), e não `rounded-control`: com uma linha o raio cobre
          // metade da altura e o campo É a pílula do `1j`; quando o texto cresce, ele
          // continua um retângulo arredondado em vez de virar uma cápsula deformada.
          className="border-border-subtle bg-surface-card text-text-primary type-body max-h-28 min-h-10 flex-1 resize-none rounded-2xl border px-4 py-2"
        />
        <button
          type="submit"
          aria-label={TEXTOS.enviar}
          disabled={conversa.pendente || rascunho.trim() === ''}
          className="bg-action-primary text-action-primary-fg hover:bg-action-primary-hover rounded-control duration-fast flex size-10 shrink-0 items-center justify-center disabled:cursor-not-allowed disabled:opacity-50"
        >
          <i className="ph ph-paper-plane-right text-xl" aria-hidden="true" />
        </button>
      </form>
    </div>
  )
}
